import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import { accountTypes } from 'config/user.type.constants'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { getAdminConfig, setAdminConfig } from 'services'
import { ToggleButton } from 'stories/components'

interface EmailRestrictionsConfig {
  accountTypes: Record<string, boolean>
  loanStatuses: Record<string, boolean>
}

type SectionKey = keyof EmailRestrictionsConfig

const defaultConfig: EmailRestrictionsConfig = {
  accountTypes: {},
  loanStatuses: {},
}

const sections: {
  key: SectionKey
  title: string
  description: string
}[] = [
  {
    key: 'accountTypes',
    title: 'Email by Receiver Account Type',
    description:
      'Disabled account types are removed from the recipients of every email. Addresses that do not belong to a user are never affected.',
  },
  {
    key: 'loanStatuses',
    title: 'Loan Email by Loan Status',
    description:
      'Disabled statuses stop loan-related emails entirely. This applies only to emails whose reply address identifies a loan.',
  },
]

export const EmailRestrictions = () => {
  const [isLoading, setLoading] = useState(false)
  const [isChanged, setChanged] = useState(false)
  const [data, setData] = useState<EmailRestrictionsConfig>(defaultConfig)
  const loanGlobalStatus = useSelector((state: any) => state.loanGlobalStatus || {})

  const options: Record<SectionKey, Record<string, string>> = {
    accountTypes,
    loanStatuses: loanGlobalStatus,
  }

  useEffect(() => {
    setLoading(true)
    getAdminConfig('emailRestrictions')
      .then((values) =>
        setData({
          accountTypes: values?.accountTypes || {},
          loanStatuses: values?.loanStatuses || {},
        }),
      )
      .finally(() => setLoading(false))
  }, [])

  const onChange = (section: SectionKey, key: string, value: boolean) => {
    const updated = cloneDeep(data)
    updated[section][key] = value
    setData(updated)
    setChanged(true)
  }

  const onChangeAll = (section: SectionKey, value: boolean) => {
    const updated = cloneDeep(data)
    Object.keys(options[section]).forEach((key) => (updated[section][key] = value))
    setData(updated)
    setChanged(true)
  }

  const onSave = async () => {
    setLoading(true)
    try {
      await setAdminConfig('emailRestrictions', data)
      setChanged(false)
      toast('Email restrictions are stored successfully.', { type: 'info' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />
      <SaveChanges show={isChanged} onSave={onSave} />

      <div className="space-y-6">
        {sections.map(({ key, title, description }, sectionIndex) => {
          const sectionOptions = options[key]
          const optionKeys = Object.keys(sectionOptions)
          const disabledCount = optionKeys.filter((option) => data[key][option] === false).length

          return (
            <div className="border rounded-lg bg-white shadow-sm overflow-hidden" key={key}>
              <div className="border-b bg-gray-50 px-4 py-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="font-semibold text-gray-900">
                    {sectionIndex + 1}. {title}
                  </p>
                  <div className="flex items-center gap-3 text-sm">
                    <span className={disabledCount ? 'text-rose-700' : 'text-gray-500'}>
                      {disabledCount ? `${disabledCount} disabled` : 'All enabled'}
                    </span>
                    <span className="text-gray-300">|</span>
                    <button
                      type="button"
                      className="text-shade-blue hover:underline"
                      onClick={() => onChangeAll(key, true)}
                    >
                      Enable all
                    </button>
                    <button
                      type="button"
                      className="text-shade-blue hover:underline"
                      onClick={() => onChangeAll(key, false)}
                    >
                      Disable all
                    </button>
                  </div>
                </div>
                <p className="pt-1 text-sm text-gray-500">{description}</p>
              </div>

              {optionKeys.length ? (
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  {optionKeys.map((option, index) => (
                    <div
                      className="flex items-center justify-between gap-4 border-b px-4 py-2 lg:odd:border-r"
                      key={option}
                    >
                      <span className="flex min-w-0 items-baseline gap-2 text-sm text-gray-700">
                        <span className="w-6 shrink-0 text-right text-gray-400">{index + 1}.</span>
                        <span className="truncate" title={sectionOptions[option]}>
                          {sectionOptions[option]}
                        </span>
                      </span>
                      <ToggleButton
                        id={`email-restrictions-${key}-${option}`}
                        value={data[key][option] !== false}
                        onChange={(value) => onChange(key, option, value)}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="px-4 py-3 text-sm text-gray-400">No options available.</p>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
